import React, { startTransition, useCallback, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { ChevronLeft, ChevronRight } from "react-bootstrap-icons";
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import CampaignList from "../../components/campaigns/CampaignList";
import CampaignSummary from "../../components/campaigns/CampaignSummary";
import RecordFilter from "../../components/record/RecordFilter";
import RecordList from "../../components/record/RecordList";
import { useError } from "../../contexts/ErrorContext";
import { useCampaign } from "../../contexts/useCampaign";
import { CampaignAPI, RecordsAPI } from "../../services/api";
import { Campaign, Execution, VideoRecord } from "../../types";

export const RecordsPage: React.FC = () => {
    const [records, setRecords] = useState<VideoRecord[]>([]);
    const [filteredRecords, setFilteredRecords] = useState<VideoRecord[]>([]);
    const [loadingRecords, setLoadingRecords] = useState<boolean>(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
    const { setSelectedCampaign, selectedCampaign, selectedNetwork } = useCampaign();
    const { setError } = useError();

    const loadRecordsByCampaign = useCallback(async (campaignId: number) => {
        setLoadingRecords(true);
        try {
            // Re-derive the error sub-statuses *before* loading the records, so the list
            // we render already carries them -- no second pass, and no need to open every
            // execution one by one. Server-side it only calls Argo (time-boxed) for recent
            // failures that still lack a sub-status, and it must not take the whole page
            // down if it fails, hence the local catch.
            const [checkResponse] = await Promise.all([
                CampaignAPI.checkAndUpdateVideos(campaignId),
                CampaignAPI.refreshExecutionSubStatuses(campaignId).catch((error) => {
                    console.error("Failed to refresh execution sub-statuses:", error);
                    return null;
                }),
            ]);
            const response = await RecordsAPI.getByCampaign(campaignId);
            const records = response.data;

            // Apply the freshly-checked upload sizes onto the loaded videos so the
            // RecordItem progress bar reflects the latest blob upload progress.
            const videoStatusById = new Map(
                checkResponse.data.results.map(result => [result.video_id, result])
            );
            records.forEach(record => {
                record.videos?.forEach(video => {
                    const status = videoStatusById.get(video.id);
                    if (status) {
                        video.size = status.size;
                        video.uploaded_size = status.uploaded_size;
                        video.is_uploaded = status.is_uploaded;
                    }
                });
            });

            // Update all executions with "started" status
            const executionsToUpdate: Array<{ recordIndex: number; executionIndex: number; executionId: number }> = [];

            records.forEach((record, recordIndex) => {
                record.executions?.forEach((execution, executionIndex) => {
                    if (execution.status === "started") {
                        executionsToUpdate.push({ recordIndex, executionIndex, executionId: execution.id });
                    }
                });
            });

            // Fetch updated execution details for all "started" executions
            if (executionsToUpdate.length > 0) {
                console.log(`Updating ${executionsToUpdate.length} executions with "started" status`);

                const updatePromises = executionsToUpdate.map(async ({ recordIndex, executionIndex, executionId }) => {
                    try {
                        const executionResponse = await RecordsAPI.getExecutionDetails(executionId);
                        return { recordIndex, executionIndex, execution: executionResponse.data };
                    } catch (error) {
                        console.error(`Failed to update execution ${executionId}:`, error);
                        return null;
                    }
                });

                const updatedExecutions = await Promise.all(updatePromises);

                // Update the records with fresh execution data
                updatedExecutions.forEach((result) => {
                    if (result) {
                        const { recordIndex, executionIndex, execution } = result;
                        records[recordIndex].executions[executionIndex] = execution;
                    }
                });
            }
            setRecords(records);
            setFilteredRecords(records); // Initially show all records
        } catch (error) {
            setError(`Error loading records by campaign:${error}`);
            setRecords([]);
            setFilteredRecords([]);
        } finally {
            setLoadingRecords(false);
        }
    }, [setError]);

    const loadRecordsByCampaignRef = React.useRef(loadRecordsByCampaign);
    useEffect(() => { loadRecordsByCampaignRef.current = loadRecordsByCampaign; }, [loadRecordsByCampaign]);

    const refreshRecord = async (recordId: number) => {
        try {
            const axiosRequest = await RecordsAPI.getRecord(recordId);
            const updatedRecord = axiosRequest.data;
            const existingRecord = records.find(r => r.id == recordId);
            let iteration = 0;
            if (existingRecord) {
                iteration = updatedRecord.iteration || 0;
            }
            updatedRecord.iteration = iteration;

            // Update both records and filteredRecords. Functional updates: several
            // refreshes can be in flight at once (e.g. batch workflow completion maps
            // over every record), and a captured array would drop all but the last.
            const merge = (list: VideoRecord[]) =>
                list.map(record => (record.id === updatedRecord.id ? updatedRecord : record));
            setRecords(merge);
            setFilteredRecords(merge);
        } catch (e) {
            setError(`Error with updating record ${recordId} ${e}`);
        }
    };

    // Merge a freshly-fetched execution into the record that owns it. RecordExecution
    // already hit /executions/<id>/, which re-syncs from Argo and persists status and
    // sub_status server-side, so there is nothing left to fetch: reusing its payload
    // keeps the record header (status badge, error sub-status) in sync with the detail
    // panel without a getRecord round trip or a full campaign reload.
    const applyExecutionUpdate = useCallback((recordId: number, execution: Execution) => {
        const merge = (list: VideoRecord[]) => list.map(record => {
            if (record.id !== recordId) return record;
            const known = record.executions.some(e => e.id === execution.id);
            return {
                ...record,
                executions: known
                    ? record.executions.map(e => (e.id === execution.id ? execution : e))
                    : [...record.executions, execution],
            };
        });
        setRecords(merge);
        setFilteredRecords(merge);
    }, []);

    const addRecord = (record: VideoRecord) => {
        const newRecords = records.concat([record]);
        setRecords(newRecords);
        setFilteredRecords(newRecords);
    };

    const onRecordUpdated = async (record: VideoRecord | null) => {
        try {
            if (record == null) {
                if (selectedCampaign) {
                    await loadRecordsByCampaign(selectedCampaign.id);
                }
                return;
            }
            const isRecordInList = records.find((r) => r.id == record.id);
            if (!isRecordInList) {
                addRecord(record);
                return;
            }
            await refreshRecord(record.id);
        } catch (e) {
            setError(`Error with updating record ${e}`);
        }
    };

    const handleFilteredRecordsChange = (filteredRecords: VideoRecord[]) => {
        if (!selectedCampaign) {
            setFilteredRecords([]);
            return;
        }
        // Allow empty results from filtering
        if (filteredRecords.length == 0) {
            setFilteredRecords([]);
            return;
        }

        // Verify records belong to current network
        const campaign_index = filteredRecords[0].campaign || 0;
        if (campaign_index == selectedCampaign.id) {
            setFilteredRecords(filteredRecords);
        }
    };


    const handleCampaignChange = (campaign: Campaign | undefined) => {
        setSelectedCampaign(campaign || null);
    };

    const toggleSidebar = () => {
        setIsSidebarCollapsed(!isSidebarCollapsed);
    };

    const refreshAll = () => {
        if (selectedCampaign) {
            loadRecordsByCampaign(selectedCampaign.id);
        }
    };

    // Load records when campaign changes
    useEffect(() => {
        if (selectedCampaign) {
            loadRecordsByCampaignRef.current(selectedCampaign.id);
        } else {
            startTransition(() => {
                setRecords([]);
                setFilteredRecords([]);
            });
        }
    }, [selectedCampaign]);

    return (
        <Row className="no-gutters" style={{ height: "100%" }}>
            {/* Sidebar */}
            <Col
                xs="auto"
                className="bg-light border-end position-relative"
                style={{ height: "100%", overflowY: "auto", width: isSidebarCollapsed ? "50px" : "300px" }}
            >
                {/* Toggle Button */}
                <Button
                    variant="light"
                    className="position-absolute"
                    style={{
                        top: "10px",
                        right: "-15px",
                        zIndex: 10,
                        borderRadius: "50%",
                        boxShadow: "0px 0px 5px rgba(0,0,0,0.2)",
                    }}
                    onClick={toggleSidebar}
                >
                    {isSidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
                </Button>
                <div className="p-3">
                    <CampaignList
                        onCampaignSelect={handleCampaignChange}
                    />
                </div>
                {(records.length > 0) && (
                    <div className="p-3 border-top">
                        <RecordFilter
                            records={records}
                            onFilteredRecordsChange={handleFilteredRecordsChange}
                        />
                    </div>
                )}
            </Col>

            {/* Main Content */}
            <Col
                className="flex-grow-1"
                style={{
                    height: "100%",
                    overflowY: "auto",
                    transition: "all 0.3s",
                }}
            >
                {!selectedNetwork || !selectedCampaign ? (
                    <div className="card-body">
                        <div className="text-center text-muted p-5">
                            <p>Please select a campaign from the sidebar to view records.</p>
                        </div>
                    </div>
                ) : (
                    <div className="card-body">
                        <CampaignSummary
                            campaign={selectedCampaign}
                            network={selectedNetwork}
                            records={records}
                            activeWorkflows={[]}
                            refresh={refreshAll}
                        />
                        <div>
                            <h5 className="mt-4 mb-3">Records</h5>
                            <p className="text-muted">
                                Showing {filteredRecords.length} records
                            </p>
                            <RecordList
                                loadingRecords={loadingRecords}
                                records={filteredRecords}
                                onRecordUpdated={onRecordUpdated}
                                onExecutionUpdated={applyExecutionUpdate}
                            />
                        </div>
                    </div>
                )}
            </Col>
        </Row>
    );
};